import { canvasHeight, canvasWidth, initialZoom, Themes } from './data/constants';
import { Camera } from './camera';
import { StageDef } from './data/maps';
import { Marble } from './marble';
import { ParticleManager } from './particleManager';
import { GameObject } from './gameObject';
import { UIObject } from './UIObject';
import { VectorLike } from './types/VectorLike';
import { MapEntityState } from './types/MapEntity.type';
import { ColorTheme } from './types/ColorTheme';
import { KeywordService } from './keywordService';

export type RenderParameters = {
  camera: Camera;
  stage: StageDef;
  entities: MapEntityState[];
  marbles: Marble[];
  winners: Marble[];
  particleManager: ParticleManager;
  effects: GameObject[];
  winnerRank: number;
  winner: Marble | null;
  size: VectorLike;
  theme: ColorTheme;
  ballImage?: HTMLImageElement; 
};

export class RouletteRenderer {
  private _canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  public sizeFactor = 1;

  private _images: { [key: string]: HTMLImageElement } = {};
  private _ballImagesPool: HTMLImageElement[] = []; // 공 이미지 저장소
  private _theme: ColorTheme = Themes.dark;
  private _keywordService: KeywordService = new KeywordService();

  constructor() {
  }

  get width() {
    return this._canvas.width;
  }

  get height() {
    return this._canvas.height;
  }

  get canvas() {
    return this._canvas;
  }

  set theme(value: ColorTheme) {
    this._theme = value;
  }

  // [수정] 이미지 로드 헬퍼 함수
  private async _loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((rs) => {
      const img = new Image();
      img.onload = () => rs(img);
      img.onerror = () => {
          console.warn(`[이미지 로드 실패] ${url}`);
          rs(new Image()); 
      };
      img.src = url;
    });
  }

  async init() {
    console.log("[RouletteRenderer] 초기화 시작...");
    // 키워드 서비스와 이미지 로드 병렬 처리
    await Promise.all([this._load(), this._keywordService.init()]);
    
    this._canvas = document.createElement('canvas');
    this._canvas.width = canvasWidth;
    this._canvas.height = canvasHeight;
    this.ctx = this._canvas.getContext('2d', {
      alpha: false,
    }) as CanvasRenderingContext2D;

    document.body.appendChild(this._canvas);

    const resizing = (entries?: ResizeObserverEntry[]) => {
      const realSize = entries
        ? entries[0].contentRect
        : this._canvas.getBoundingClientRect();
      const width = Math.max(realSize.width / 2, 640);
      const height = (width / realSize.width) * realSize.height;
      this._canvas.width = width;
      this._canvas.height = height;
      this.sizeFactor = width / realSize.width;
    };

    const resizeObserver = new ResizeObserver(resizing);
    resizeObserver.observe(this._canvas);
    resizing();
  }

  private async _load(): Promise<void> {
    const loadPromises: Promise<void>[] = [];

    // 1. 특정 이름 이미지 로드
    const namedImages = [
      { name: '챔루', imgUrl: new URL('../assets/images/chamru.png', import.meta.url) },
      { name: '쿠빈', imgUrl: new URL('../assets/images/kubin.png', import.meta.url) },
    ];

    namedImages.forEach(({ name, imgUrl }) => {
      loadPromises.push((async () => {
        try {
           const img = await this._loadImage(imgUrl.toString());
           if(img.width > 0) this._images[name] = img;
        } catch (e) { }
      })());
    });

    loadPromises.push((async () => {
        try {
          await this._loadImage(new URL('../assets/images/ff.svg', import.meta.url).toString());
        } catch (e) {}
    })());

    // 2. [수정] 공 이미지 로드 (1.png ~ 9.png)
    const ballUrls = [
        new URL('../assets/images/balls/1.png', import.meta.url),
        new URL('../assets/images/balls/2.png', import.meta.url),
        new URL('../assets/images/balls/3.png', import.meta.url),
        new URL('../assets/images/balls/4.png', import.meta.url),
        new URL('../assets/images/balls/5.png', import.meta.url),
        new URL('../assets/images/balls/6.png', import.meta.url),
        new URL('../assets/images/balls/7.png', import.meta.url),
        new URL('../assets/images/balls/8.png', import.meta.url),
        new URL('../assets/images/balls/9.png', import.meta.url),
    ];

    const loadedImages = await Promise.all(
        ballUrls.map(url => this._loadImage(url.toString()))
    );

    loadedImages.forEach(img => {
        if (img.width > 0) this._ballImagesPool.push(img);
    });

    await Promise.all(loadPromises);
  }

  private getMarbleImage(name: string): CanvasImageSource | undefined {
    if (this._images[name]) return this._images[name];
    // [수정] 에러 수정: getImageForName -> getSprite
    return this._keywordService.getSprite(name);
  }

  render(renderParameters: RenderParameters, uiObjects: UIObject[]) {
    this._theme = renderParameters.theme;
    this.ctx.fillStyle = this._theme.background; // [수정] bgColor -> background
    this.ctx.fillRect(0, 0, this._canvas.width, this._canvas.height);

    this.ctx.save();
    this.ctx.scale(initialZoom, initialZoom);
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';
    this.ctx.font = '0.4pt Jua'; // [수정] 디버그 폰트도 Jua로 변경
    this.ctx.lineWidth = 3 / (renderParameters.camera.zoom + initialZoom);
    renderParameters.camera.renderScene(this.ctx, () => {
      this.renderEntities(renderParameters.entities);
      this.renderEffects(renderParameters);
      this.renderMarbles(renderParameters);
    });
    this.ctx.restore();

    uiObjects.forEach((obj) =>
      obj.render(
        this.ctx,
        renderParameters,
        this._canvas.width,
        this._canvas.height,
      ),
    );
    renderParameters.particleManager.render(this.ctx);
    this.renderWinner(renderParameters);
  }

  private renderEntities(entities: MapEntityState[]) {
    this.ctx.save();
    entities.forEach((entity) => {
      const transform = this.ctx.getTransform();
      // [수정] position.x -> x 로 변경 (에러 해결)
      this.ctx.translate(entity.x, entity.y);
      this.ctx.rotate(entity.angle);
      const shape = entity.shape;
      // [수정] color -> shape.color (에러 해결)
      this.ctx.fillStyle = shape.color ?? this._theme.entity[shape.type].fill;
      this.ctx.strokeStyle = shape.color ?? this._theme.entity[shape.type].outline;
      this.ctx.shadowBlur = this._theme.entity[shape.type].bloomRadius;
      this.ctx.shadowColor = shape.bloomColor ?? shape.color ?? this._theme.entity[shape.type].bloom;
      
      switch (shape.type) {
        case 'polyline':
          if (shape.points.length > 0) {
            this.ctx.beginPath();
            this.ctx.moveTo(shape.points[0][0], shape.points[0][1]);
            for (let i = 1; i < shape.points.length; i++) {
              this.ctx.lineTo(shape.points[i][0], shape.points[i][1]);
            }
            this.ctx.stroke();
          }
          break;
        case 'box':
          const w = shape.width * 2;
          const h = shape.height * 2;
          this.ctx.rotate(shape.rotation);
          this.ctx.fillRect(-w / 2, -h / 2, w, h);
          this.ctx.strokeRect(-w / 2, -h / 2, w, h);
          break;
        case 'circle':
          this.ctx.beginPath();
          this.ctx.arc(0, 0, shape.radius, 0, Math.PI * 2, false);
          this.ctx.stroke();
          break;
      }
      this.ctx.setTransform(transform);
    });
    this.ctx.restore();
  }

  private renderEffects({ effects, camera }: RenderParameters) {
    effects.forEach((effect) =>
      effect.render(this.ctx, camera.zoom * initialZoom, this._theme),
    );
  }

  private renderMarbles({
                          marbles,
                          camera,
                          winnerRank,
                          winners,
                          size,
                          ballImage
                        }: RenderParameters) {
    const winnerIndex = winnerRank - winners.length;
    const viewPort = { x: camera.x, y: camera.y, w: size.x, h: size.y, zoom: camera.zoom * initialZoom };
    
    marbles.forEach((marble, i) => {
      let imageToUse = this.getMarbleImage(marble.name);

      // [수정] 공 이미지 풀에서 가져오기 로직 추가
      if (!imageToUse && this._ballImagesPool.length > 0) {
          imageToUse = this._ballImagesPool[marble.id % this._ballImagesPool.length];
      }
      
      if (!imageToUse) {
          imageToUse = ballImage;
      }

      // [수정] 원래 인자 개수 7개 유지
      marble.render(
        this.ctx,
        camera.zoom * initialZoom,
        i === winnerIndex,
        false,
        imageToUse,
        viewPort,
        this._theme,
      );
    });
  }

  private renderWinner({ winner, theme }: RenderParameters) {
    if (!winner) return;
    this.ctx.save();
    this.ctx.fillStyle = theme.winnerBackground;
    this.ctx.fillRect(
      this._canvas.width / 2,
      this._canvas.height - 168,
      this._canvas.width / 2,
      168,
    );

    const marbleSize = 100;
    const marbleCenterX = this._canvas.width - marbleSize / 2 - 20;
    const marbleCenterY = this._canvas.height - 168 / 2;
    
    let marbleImage = this.getMarbleImage(winner.name);
    // [수정] 우승자 이미지 풀 로직 추가
    if (!marbleImage && this._ballImagesPool.length > 0) {
        marbleImage = this._ballImagesPool[winner.id % this._ballImagesPool.length];
    }

    if (marbleImage) {
      this.ctx.drawImage(
        marbleImage as CanvasImageSource,
        marbleCenterX - marbleSize / 2,
        marbleCenterY - marbleSize / 2,
        marbleSize,
        marbleSize,
      );
    } else {
      this.ctx.beginPath();
      this.ctx.arc(marbleCenterX, marbleCenterY, marbleSize / 2, 0, Math.PI * 2);
      this.ctx.fillStyle = `hsl(${winner.hue} 100% ${theme.marbleLightness})`;
      this.ctx.fill();
    }

    this.ctx.fillStyle = theme.winnerText;
    this.ctx.strokeStyle = theme.winnerOutline;
    
    // [수정] 우승자 텍스트 Jua 폰트로 변경
    this.ctx.font = '48px Jua'; 
    this.ctx.textAlign = 'right';
    this.ctx.lineWidth = 4;
    const textRightX = marbleCenterX - marbleSize / 2 - 20;
    
    if (theme.winnerOutline) {
      this.ctx.strokeText('Winner', textRightX, this._canvas.height - 120);
    }
    this.ctx.fillText('Winner', textRightX, this._canvas.height - 120);

    // [수정] 우승자 이름 Jua 폰트로 변경
    this.ctx.font = '72px Jua';
    this.ctx.fillStyle = `hsl(${winner.hue} 100% ${theme.marbleLightness})`;
    
    if (theme.winnerOutline) {
      this.ctx.strokeText(winner.name, textRightX, this._canvas.height - 55);
    }
    this.ctx.fillText(winner.name, textRightX, this._canvas.height - 55);
    this.ctx.restore();
  }
}